import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { getActivePO } from '../lib/storeMode';
import { getActiveHoliday } from '../lib/holiday';
import { getStockMap, resolveStock, isOutOfStock } from '../lib/stock';
import { withTimeout } from '../lib/withTimeout';
import ClosedScreen from '../components/ClosedScreen';
import StoreNav from '../components/StoreNav';

const WA_NUM = process.env.REACT_APP_WA_BUSINESS_NUMBER || '6287722585231';
const BRAND_NAME = process.env.REACT_APP_BRAND_NAME || 'HAPin';
const TAGLINE = process.env.REACT_APP_TAGLINE || 'Sekali HAP, langsung laHAP';

const SUPPORT = {
  email:   process.env.REACT_APP_SUPPORT_EMAIL   || 'sapan.catering@gmail.com',
  phone:   process.env.REACT_APP_SUPPORT_PHONE   || '+62 877-2258-5231',
  address: process.env.REACT_APP_SUPPORT_ADDRESS || 'Pamekasan, Jawa Timur, Indonesia',
};

const PERKS = [
  { title: 'Bayar di Tempat', desc: 'Pesan dulu, bayar tunai saat pesanan sampai atau kamu ambil sendiri.' },
  { title: 'Antar / Ambil Sendiri', desc: 'Pilih diantar ke lokasimu atau ambil langsung di outlet kami.' },
  { title: 'Selalu Segar', desc: 'Diproduksi sehari sebelum jadwal kirim, bukan stok gudang berhari-hari.' },
];

function fmtDate(d) {
  return new Date(d).toLocaleDateString('id-ID', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
}

export default function Home() {
  // mode: null = masih dicek, 'po' = ada PO buka, 'pos' = menu reguler
  const [mode, setMode] = useState(null);
  const [po, setPO] = useState(null);
  const [featuredMenu, setFeaturedMenu] = useState([]);
  const [heroMenu, setHeroMenu] = useState(null);
  const [stockMap, setStockMap] = useState({});
  const [holiday, setHoliday] = useState(undefined); // undefined = belum dicek, null = tidak libur, object = libur
  const [refName, setRefName] = useState('');
  const nav = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const refCode = searchParams.get('ref');
    if (refCode) {
      localStorage.setItem('sapan_ref', refCode.toLowerCase().trim());
      lookupRef(refCode.toLowerCase().trim());
    } else {
      const saved = localStorage.getItem('sapan_ref');
      if (saved) lookupRef(saved);
    }
    load();
    // eslint-disable-next-line
  }, []);

  async function lookupRef(code) {
    const { data } = await supabase.from('users').select('name').eq('ref_code', code).eq('is_active', true).maybeSingle();
    if (data?.name) setRefName(data.name);
  }

  async function load() {
    // "Pilihan · Dari dapur kami" tidak perlu nunggu apa-apa, langsung jalan.
    loadFeaturedMenu();

    // Cek libur & PO aktif dijalankan BARENGAN (bukan berurutan) biar lebih cepat,
    // dan masing-masing dikasih batas waktu (timeout) + fail-open supaya kalau
    // koneksi lambat/gagal, toko tetap tampil normal — nggak macet di "Memuat...".
    const [activeHoliday, activePO] = await Promise.all([
      withTimeout(getActiveHoliday(), 6000, null),
      withTimeout(getActivePO(), 6000, null),
    ]);

    setHoliday(activeHoliday || null);
    if (activeHoliday) return;

    if (activePO && activePO.product) {
      setPO(activePO);
      setMode('po');
      return;
    }
    // Tidak ada PO buka -> tampilkan hero banner sesuai setting admin
    loadHeroMenu();
    setMode('pos');
  }

  async function loadHeroMenu() {
    const { data } = await supabase
      .from('menu_items')
      .select('*')
      .eq('is_hero', true)
      .eq('is_hidden', false)
      .not('image_url', 'is', null)
      .limit(1)
      .maybeSingle();
    setHeroMenu(data || null);
  }

  async function loadFeaturedMenu() {
    const { data } = await supabase
      .from('menu_items')
      .select('*')
      .eq('is_featured', true)
      .eq('is_hidden', false)
      .not('image_url', 'is', null)
      .order('featured_order')
      .order('name')
      .limit(4);
    setFeaturedMenu(data || []);
    const map = await getStockMap((data || []).map(m => m.id));
    setStockMap(map);
  }

  function handleOrder() {
    if (mode === 'po') nav(po?.product ? `/product/${po.product.id}` : '/');
    else if (heroMenu) nav(`/menu/${heroMenu.id}`);
    else document.getElementById('menu')?.scrollIntoView({ behavior: 'smooth' });
  }

  if (holiday === undefined) {
    return (
      <div className="store" style={{ minHeight: '100vh' }}>
        <StoreNav />
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--ink-soft)' }}>Memuat...</div>
      </div>
    );
  }

  if (holiday) {
    return <ClosedScreen holiday={holiday} />;
  }

  if (mode === null) {
    return (
      <div className="store" style={{ minHeight: '100vh' }}>
        <StoreNav />
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--ink-soft)' }}>Memuat...</div>
      </div>
    );
  }

  // Prioritas gambar hero: produk PO yang sedang aktif, kalau tidak ada baru pakai
  // menu yang ditandai admin sebagai "Hero Banner" (is_hero) di halaman Menu POS.
  const heroImageUrl = mode === 'po' ? (po.product?.image_url || null) : (heroMenu?.image_url || null);
  const heroImageAlt = mode === 'po' ? po.product?.name : (heroMenu?.name || BRAND_NAME);

  return (
    <div className="store" style={{ minHeight: '100vh' }}>
      <StoreNav />

      {/* ── Hero ── */}
      <section style={{ maxWidth: 1040, margin: '0 auto', padding: '56px 20px 40px', display: 'grid', gridTemplateColumns: '1.05fr 1fr', gap: 48, alignItems: 'center' }}
        className="hero-grid">
        <div className="fade-up">
          <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 11, letterSpacing: 3, textTransform: 'uppercase', color: 'var(--primary)', display: 'block', marginBottom: 16 }}>
            {mode === 'po' ? `Pre-Order · ${fmtDate(po.date)}` : TAGLINE}
          </span>
          <h1 style={{ fontFamily: "'Baloo 2',sans-serif", fontWeight: 700, fontSize: 'clamp(2.4rem,5vw,3.4rem)', lineHeight: 1.06, color: 'var(--ink)', marginBottom: 20 }}>
            {mode === 'po' ? (
              <>Yuk ikutan PO,<br /><span style={{ color: 'var(--primary)' }}>{po.product.name}!</span></>
            ) : (
              <>Camilan gurih,<br /><span style={{ color: 'var(--primary)' }}>siap kapan saja.</span></>
            )}
          </h1>
          <p style={{ fontSize: '1rem', color: 'var(--ink-soft)', lineHeight: 1.7, marginBottom: 28, maxWidth: 420 }}>
            {mode === 'po'
              ? `Ambil di ${po.area?.name || 'lokasi meet point kami'}. Pesan sekarang sebelum PO ditutup.`
              : 'Pesan sekarang, pilih ambil sendiri atau diantar, bayar tunai saat pesanan di tangan.'}
          </p>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
            <button onClick={handleOrder} style={{
              display: 'inline-flex', alignItems: 'center', gap: 8, background: 'var(--primary)', color: '#fff',
              border: 'none', borderRadius: 999, padding: '13px 26px', fontSize: '.88rem', fontWeight: 700,
            }}>
              Pesan Sekarang
            </button>
          </div>

          {mode === 'po' && (
            <p style={{ fontSize: '.82rem', color: 'var(--ink-soft)', marginTop: 16 }}>
              Nggak mau nunggu PO?{' '}
              <button onClick={() => document.getElementById('menu')?.scrollIntoView({ behavior: 'smooth' })} style={{
                background: 'none', border: 'none', padding: 0, color: 'var(--primary)', fontWeight: 700, textDecoration: 'underline', cursor: 'pointer', fontSize: 'inherit', fontFamily: 'inherit',
              }}>
                Beli menu siap makan kami di bawah
              </button>, bisa langsung dipesan hari ini tanpa nunggu batch.
            </p>
          )}
        </div>

        <div className="fade-up" style={{ animationDelay: '.08s' }}>
          <div style={{
            width: '100%', aspectRatio: '4/5', borderRadius: 24, overflow: 'hidden',
            background: heroImageUrl ? undefined : 'linear-gradient(150deg,#3a2b1f,#211710)',
            position: 'relative',
          }}>
            {heroImageUrl ? (
              <img src={heroImageUrl} alt={heroImageAlt} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
            ) : (
              <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span style={{ fontFamily: "'Baloo 2',sans-serif", fontWeight: 700, fontSize: '1.3rem', color: 'rgba(251,247,241,.5)' }}>{BRAND_NAME}</span>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ── Banner referral ── */}
      {refName && (
        <div style={{ maxWidth: 1040, margin: '0 auto', padding: '0 20px' }}>
          <div style={{ background: 'var(--primary-soft)', border: '1px solid rgba(227,91,42,.25)', borderRadius: 12, padding: '10px 16px', marginBottom: 20, fontSize: '.82rem', color: 'var(--primary-hover)' }}>
            Kamu direkomendasikan oleh <strong>{refName}</strong>. Order kamu akan tercatat atas nama beliau.
          </div>
        </div>
      )}

      {featuredMenu.length > 0 && (
        <section id="menu" style={{ maxWidth: 1040, margin: '0 auto', padding: '8px 20px 48px' }}>
          <div style={{ marginBottom: 22 }}>
            <span style={{ display: 'inline-block', background: 'var(--ink)', color: '#fff', fontSize: '.66rem', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', padding: '5px 12px', borderRadius: 999, marginBottom: 12 }}>
              Pilihan
            </span>
            <h2 style={{ fontFamily: "'Baloo 2',sans-serif", fontWeight: 700, fontSize: '1.8rem', color: 'var(--ink)' }}>
              Dari <span style={{ fontStyle: 'italic', fontFamily: "'DM Sans',sans-serif", fontWeight: 400 }}>dapur kami</span>
            </h2>
          </div>

          <div className="featured-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 18 }}>
            {featuredMenu.map(m => {
              const stockRow = resolveStock(stockMap, m.id, null);
              const outOfStock = isOutOfStock(stockRow);
              return (
                <button key={m.id} onClick={() => nav(`/menu/${m.id}`)} style={{
                  textAlign: 'left', background: 'transparent', border: 'none', padding: 0, cursor: 'pointer',
                }}>
                  <div style={{ position: 'relative', width: '100%', aspectRatio: '3/4', borderRadius: 18, overflow: 'hidden', marginBottom: 12 }}>
                    <img src={m.image_url} alt={m.name} style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block', filter: outOfStock ? 'grayscale(.7)' : undefined, opacity: outOfStock ? .55 : 1 }} />
                    <span style={{
                      position: 'absolute', top: 12, left: 12, background: 'rgba(251,247,241,.9)', color: 'var(--ink)',
                      fontSize: '.64rem', fontWeight: 700, letterSpacing: '.5px', textTransform: 'uppercase',
                      padding: '5px 10px', borderRadius: 999,
                    }}>
                      {m.featured_label || 'Favorit'}
                    </span>
                    {outOfStock && (
                      <span style={{
                        position: 'absolute', bottom: 12, left: 12, right: 12, textAlign: 'center',
                        background: 'rgba(33,23,16,.85)', color: '#fff',
                        fontSize: '.66rem', fontWeight: 700, letterSpacing: '.5px', textTransform: 'uppercase',
                        padding: '6px 10px', borderRadius: 999,
                      }}>
                        Stok Habis
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </section>
      )}

      {/* ── Kenapa kami ── */}
      <section id="kenapa-kami" style={{ background: 'var(--sand)', padding: '48px 20px 64px' }}>
        <div style={{ maxWidth: 1040, margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 28 }} className="perks-grid">
          {PERKS.map(p => (
            <div key={p.title}>
              <div style={{ width: 34, height: 2, background: 'var(--primary)', marginBottom: 14 }} />
              <div style={{ fontFamily: "'Baloo 2',sans-serif", fontWeight: 700, fontSize: '1.15rem', marginBottom: 6, color: 'var(--ink)' }}>{p.title}</div>
              <div style={{ fontSize: '.86rem', color: 'var(--ink-soft)', lineHeight: 1.6 }}>{p.desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Footer ── */}
      <footer style={{ background: 'var(--ink)', color: 'rgba(251,247,241,.55)', padding: '36px 20px' }}>
        <div style={{ maxWidth: 1040, margin: '0 auto', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 }}>
          <div style={{ fontFamily: "'Baloo 2',sans-serif", fontWeight: 700, fontSize: '1.15rem', color: '#FBF7F1' }}>{BRAND_NAME}</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 18, fontSize: '.78rem' }}>
            <a href={`mailto:${SUPPORT.email}`} style={{ color: 'rgba(251,247,241,.7)' }}>{SUPPORT.email}</a>
                        <span>{SUPPORT.address}</span>
            <a href={`https://wa.me/${WA_NUM}`} target="_blank" rel="noreferrer" style={{ color: '#8FD9A8' }}>Chat WhatsApp</a>
          </div>
          <div style={{ fontFamily: "'DM Mono',monospace", fontSize: '.62rem', letterSpacing: 1.5, textTransform: 'uppercase', color: 'rgba(251,247,241,.25)', borderTop: '1px solid rgba(251,247,241,.08)', paddingTop: 14, width: '100%', textAlign: 'center' }}>
            © {new Date().getFullYear()} {BRAND_NAME} · {TAGLINE}
          </div>
        </div>
      </footer>

      <style>{`
        @media (max-width: 760px) {
          .hero-grid { grid-template-columns: 1fr !important; }
          .perks-grid { grid-template-columns: 1fr !important; gap: 24px !important; }
          .featured-grid { grid-template-columns: repeat(2,1fr) !important; }
        }
        @media (max-width: 460px) {
          .featured-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
}
